import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, History, Mail, MailWarning, MailCheck } from "lucide-react";
import { api, MOVEMENT_LABELS, MOVEMENT_COLORS, fmtDateTime, locationLabel } from "../lib/api";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { MovementDetailDialog } from "../components/MovementDetail";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function EmailStatus({ status }) {
  if (status === "enviado") return <span className="flex items-center gap-1 text-emerald-600 text-xs" data-testid="email-status-enviado"><MailCheck className="w-3.5 h-3.5" /> Correo enviado</span>;
  if (status === "fallido") return <span className="flex items-center gap-1 text-red-600 text-xs" data-testid="email-status-fallido"><MailWarning className="w-3.5 h-3.5" /> Correo fallido</span>;
  return <span className="flex items-center gap-1 text-slate-400 text-xs" data-testid="email-status-pendiente"><Mail className="w-3.5 h-3.5" /> Enviando...</span>;
}

export default function Historial() {
  const [q, setQ] = useState("");
  const [type, setType] = useState("todos");
  const [selectedId, setSelectedId] = useState(null);

  const { data: movements = [], isLoading } = useQuery({
    queryKey: ["movements", q, type],
    queryFn: async () => (await api.get("/movements", { params: { q, movement_type: type === "todos" ? "" : type } })).data,
    refetchInterval: (query) =>
      Array.isArray(query.state.data) && query.state.data.some((m) => m.email_status === "pendiente") ? 4000 : false,
  });

  return (
    <div className="space-y-5" data-testid="historial-page">
      <div>
        <h1 className="font-outfit text-2xl md:text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <History className="w-6 h-6 text-cyan-600" /> Trazabilidad e Historial
        </h1>
        <p className="text-sm text-slate-500">Busque por número de contenedor, matrícula de camión, conductor, sello o ID de movimiento</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar contenedor, camión o conductor..."
                 className="pl-9 h-11" data-testid="history-search-input" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="sm:w-56 h-11" data-testid="history-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos" data-testid="filter-todos">Todos los tipos</SelectItem>
            {Object.entries(MOVEMENT_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k} data-testid={`filter-${k}`}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading && <p className="text-sm text-slate-400" data-testid="history-loading">Cargando movimientos...</p>}
      {!isLoading && movements.length === 0 && (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-slate-400 text-sm" data-testid="history-empty">Sin resultados para la búsqueda</CardContent></Card>
      )}

      <div className="space-y-2">
        {movements.map((m) => (
          <button key={m.id} onClick={() => setSelectedId(m.id)} data-testid={`movement-row-${m.movement_id}`}
                  className="w-full text-left bg-white border border-slate-200 rounded-xl px-4 py-3 hover:border-cyan-400 hover:shadow-sm transition-all">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <span className="font-mono font-bold tracking-wider text-navy bg-slate-100 px-2.5 py-1 rounded border border-slate-200">{m.container_number}</span>
                <Badge variant="outline" className={`${MOVEMENT_COLORS[m.movement_type]} text-[11px]`}>{MOVEMENT_LABELS[m.movement_type]}</Badge>
              </div>
              <span className="text-xs text-slate-400 font-mono">{m.movement_id}</span>
            </div>
            <div className="mt-2 flex items-center gap-4 flex-wrap text-xs text-slate-500">
              <span>{m.operation_type} #{m.operation_number}</span>
              <span>{locationLabel(m)}</span>
              <span>{m.truck_matricula ? `Camión ${m.truck_matricula}` : "Sin camión"}</span>
              <span>{m.driver_nombre || "Sin conductor"}</span>
              <span>{fmtDateTime(m.created_at)}</span>
              <EmailStatus status={m.email_status} />
            </div>
          </button>
        ))}
      </div>

      <MovementDetailDialog movementId={selectedId} open={!!selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}
